/**
 * One shared EventSource per tab for a stream path, fanned out to subscribers.
 * Auth: the httponly cookie (withCredentials). When the app runs on the Bearer
 * fallback (Safari), it trades the login token for a single-use 60s stream pass
 * (POST /auth/stream-ticket) so the token never goes in the URL. A pass can't be
 * reused, so ticketed streams reconnect themselves with a fresh pass (backoff).
 */
import { apiClient } from '@/api/client';
import { getAccessToken } from '@/lib/authToken';

const MAX_RETRY_MS = 30000;

export function createSharedStream({ path, eventTypes, reportStatus = false, replay = false }) {
  let es = null;
  let opening = false;
  let refCount = 0;
  let timer = null;
  let retryMs = 1000;
  let lastEventId = '';
  const listeners = new Set();

  const notify = (payload) => listeners.forEach((fn) => {
    try { fn(payload); } catch { /* isolate listener errors */ }
  });
  const status = (healthy) => { if (reportStatus) notify({ type: 'stream.status', healthy }); };

  const retry = () => {
    clearTimeout(timer);
    timer = setTimeout(open, retryMs);
    retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
  };

  async function open() {
    if (es || opening || refCount <= 0) return;
    const params = new URLSearchParams();
    const ticketed = Boolean(getAccessToken());
    if (ticketed) {
      opening = true;
      try {
        const { data } = await apiClient.post('/auth/stream-ticket');
        params.set('ticket', data.ticket);
      } catch {
        opening = false;
        status(false);
        retry();
        return;
      }
      opening = false;
      if (refCount <= 0 || es) return;
      if (replay && lastEventId) params.set('last_event_id', lastEventId);
    }
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const qs = params.toString();
    es = new EventSource(`${base}${path}${qs ? `?${qs}` : ''}`, { withCredentials: true });
    eventTypes.forEach((t) => es.addEventListener(t, (e) => {
      if (e.lastEventId) lastEventId = e.lastEventId;
      let data = {};
      try { data = JSON.parse(e.data); } catch { /* keepalive / malformed */ }
      notify({ type: t, ...data });
    }));
    es.onopen = () => { retryMs = 1000; status(true); };
    es.onerror = () => {
      status(false);
      if (!ticketed) return; // cookie auth: EventSource's own reconnect works
      es.close();
      es = null;
      retry();
    };
  }

  function close() {
    clearTimeout(timer);
    if (es) { es.close(); es = null; }
  }

  return function subscribe(fn) {
    listeners.add(fn);
    refCount += 1;
    open();
    return () => {
      listeners.delete(fn);
      refCount -= 1;
      if (refCount <= 0) close();
    };
  };
}
